import { useRef, useEffect, useState } from "react";

interface SplitTextProps {
  text: string;
  className?: string;
  delay?: number;
  duration?: number;
  startDelay?: number;
}

const SplitText: React.FC<SplitTextProps> = ({
  text,
  className = "",
  delay = 50, // Reduced from 100
  duration = 400, // Reduced from 800
  startDelay = 200, // Reduced from 500
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          // Add a delay before starting the animation
          setTimeout(() => {
            setIsVisible(true);
          }, startDelay);
        }
      },
      { threshold: 0.1 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [startDelay]);

  const chars = text.split('').map((char, index) => {
    const isSpace = char === ' ';
    return {
      char: isSpace ? '\u00A0' : char, // Non-breaking space for spaces
      index,
      isSpace
    };
  });

  return (
    <div 
      ref={ref} 
      className={`split-text-container ${className}`}
      style={{ display: "inline-block" }}
    >
      {chars.map(({ char, index, isSpace }) => (
        <span
          key={index}
          className={`
            inline-block 
            transition-all 
            ease-out
            transform-gpu
            ${isVisible 
              ? 'opacity-100 translate-y-0 rotate-x-0 scale-100' 
              : 'opacity-0 translate-y-8 -rotate-x-90 scale-75'
            }
          `}
          style={{
            transitionDelay: `${index * delay}ms`,
            transitionDuration: `${duration}ms`,
            transformStyle: 'preserve-3d',
            // Ensure spaces don't collapse
            whiteSpace: isSpace ? 'pre' : 'normal',
          }}
        >
          {char}
        </span>
      ))}
      
      <style jsx>{`
        .split-text-container {
          perspective: 1000px;
        }
        
        .split-text-container span {
          transform-origin: center bottom;
          backface-visibility: hidden;
        }
        
        /* Custom 3D rotation classes */
        .-rotate-x-90 {
          transform: rotateX(-90deg) translateY(2rem) scale(0.75);
        }
        
        .rotate-x-0 {
          transform: rotateX(0deg) translateY(0) scale(1);
        }
      `}</style>
    </div>
  );
};

export default SplitText;